import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";

import { Loading } from "@lopie/kernel-ui";

import { failureText } from "../lib/reports.js";

/**
 * The versions a report has been through, newest first, and a way back to one.
 *
 * Putting one back is an edit, so it becomes the newest version and the one it
 * replaced is still in this list: a rollback that was itself a mistake can be
 * rolled back. Only the owner is shown this; the kernel keeps the last twenty.
 */
export default function HistoryDialog({ item, load, onClose, onRollback }) {
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!item) return undefined;
    let live = true;
    setReport(null);
    setError("");
    load(item.id).then((r) => live && setReport(r)).catch((e) => live && setError(failureText(e)));
    return () => { live = false; };
  }, [item, load]);

  const back = async (version) => {
    setBusy(true);
    setError("");
    try {
      await onRollback(report, version);
      onClose();
    } catch (e) {
      setError(failureText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={Boolean(item)} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="history-title">
      <DialogTitle id="history-title" sx={{ overflowWrap: "anywhere" }}>Versions of “{item?.name}”</DialogTitle>
      <DialogContent dividers>
        {!report && !error ? <Loading label="Reading its versions" minHeight={100} /> : null}
        {error ? <Alert severity="warning" role="alert">{error}</Alert> : null}
        {report ? (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              This is version {report.version}, called “{report.name}”.
            </Typography>
            {report.history?.length ? (
              <List dense aria-label="Earlier versions">
                {report.history.map((h) => (
                  <ListItem
                    key={h.version} divider
                    secondaryAction={(
                      <Button size="small" disabled={busy} onClick={() => back(h.version)}
                        aria-label={`Put version ${h.version} back`}>
                        Put this back
                      </Button>
                    )}
                  >
                    <ListItemText
                      primary={`Version ${h.version}: ${h.name}`}
                      secondary={`${h.by} · ${new Date(h.at).toLocaleString()}`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">No earlier versions: it has not been edited.</Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              The last 20 versions are kept. Putting one back makes it the newest, and the one it replaces stays here.
            </Typography>
          </>
        ) : null}
      </DialogContent>
      <DialogActions><Button onClick={onClose} disabled={busy}>Close</Button></DialogActions>
    </Dialog>
  );
}
